import { Group, Text, Button, Box } from '@mantine/core';
import { AlertTriangle } from 'react-feather';
import { notifications } from '@mantine/notifications';
import { useImpersonationStore } from '../stores/impersonation';
import { useAuthStore } from '../stores/auth';
import { usePersonaStore } from '../stores/persona';
import { authService } from '../services/api/auth';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

export function ImpersonationBanner() {
  const {
    isImpersonating,
    impersonatedUserName,
    impersonatedUserRole,
    originalRefreshToken,
    mode,
    endImpersonation,
  } = useImpersonationStore();
  const { setAuth, accessToken, refreshToken } = useAuthStore();
  const { resetToDefault, setIsAdmin, setIsFullAdmin } = usePersonaStore();
  const navigate = useNavigate();
  const [ending, setEnding] = useState(false);

  if (!isImpersonating) {
    return null;
  }

  // A non-staff impersonation (coach, participant, partner) cannot open
  // Cohort-Manager screens, so those screens return a "will not load" error.
  // Warn about it up front, so an admin does not read the error as the real
  // user's own problem. Key off the impersonated role label, NOT activePersona:
  // activePersona stays 'admin' during impersonation, so it never matched here.
  // A Cohort Manager impersonation CAN open those screens, so exclude it.
  const managerScreensBlocked =
    impersonatedUserRole !== 'Medley Cohort Manager' &&
    impersonatedUserRole !== 'Medley Admin';

  // Frontend-only impersonation (e.g., partner contacts) has no backend
  // session. MED-1383: read the stored mode. Comparing the refresh tokens
  // broke after any rotation. A session stored before the mode existed falls
  // back to the old comparison.
  const isFrontendOnly = mode ? mode === 'frontend' : refreshToken === originalRefreshToken;

  const restoreAdmin = () => {
    resetToDefault();
    setIsAdmin(true);
    setIsFullAdmin(true);
  };

  const handleEndImpersonation = async () => {
    setEnding(true);
    try {
      if (isFrontendOnly) {
        // Frontend-only impersonation (partner contacts) — just restore state
        restoreAdmin();
        const original = endImpersonation();
        if (original) {
          // MED-1383: the tokens in use are the admin's own, and a refresh
          // may have rotated the stored originals out. Keep the live pair.
          setAuth(
            original.originalUser,
            accessToken ?? original.originalAccessToken,
            refreshToken ?? original.originalRefreshToken
          );
        }
        setTimeout(() => navigate('/'), 0);
      } else {
        // Full backend impersonation — call API to end session and get fresh admin tokens
        const response = await authService.endImpersonate(
          refreshToken || undefined,
          originalRefreshToken || undefined
        );
        restoreAdmin();
        endImpersonation();
        setAuth(response.user, response.tokens.accessToken, response.tokens.refreshToken);
        setTimeout(() => navigate('/'), 0);
      }
    } catch {
      if (!isFrontendOnly) {
        // The API may have revoked the original refresh token before the
        // response was lost, so the admin login cannot be restored. Log out:
        // logout() revokes the impersonation and admin tokens it still holds.
        await useAuthStore.getState().logout();
        notifications.show({
          title: 'Log in again',
          message: 'The impersonation did not end cleanly, so you are logged out.',
          color: 'red',
        });
        navigate('/login');
        return;
      }
      // Fallback: restore from stored original state
      restoreAdmin();
      const original = endImpersonation();
      if (original) {
        setAuth(original.originalUser, original.originalAccessToken, original.originalRefreshToken);
        setTimeout(() => navigate('/'), 0);
      }
    } finally {
      setEnding(false);
    }
  };

  return (
    <Box
      style={{
        backgroundColor: '#D64545',
        color: 'white',
        padding: '6px 16px',
      }}
    >
      <Group justify="center" gap="md">
        <Group gap="xs">
          <AlertTriangle size={16} />
          <Text size="sm" fw={600}>
            You are impersonating {impersonatedUserName} ({impersonatedUserRole})
          </Text>
        </Group>
        <Button
          size="xs"
          variant="white"
          color="dark"
          onClick={handleEndImpersonation}
          loading={ending}
        >
          End Impersonation
        </Button>
      </Group>
      {managerScreensBlocked && (
        <Text size="xs" ta="center" mt={2} style={{ opacity: 0.9 }}>
          While you view as {impersonatedUserRole}, Cohort-Manager screens will not open. The real
          user never sees these errors. To edit this data, exit impersonation and open it in the
          Cohort Manager view.
        </Text>
      )}
    </Box>
  );
}

export default ImpersonationBanner;
